import { Menu, X, Smartphone, UsersRound, Warehouse, LogOut } from "lucide-react";
import { ReleaseNoteAdmin } from "@/components/ReleaseNote";
import SyncOverview from "@/components/SyncOverview";

export default function Sidebar({ xl }) {
  const {
    sidebarOpen, setSidebarOpen, TAB_GROUPS, TABS, user, tabLevel, tab, setTab,
    setRemoteBadge, remoteBadge, navigate, online, roleLabel, flushSave, logout,
    APP_VERSION, APP_BUILD,
  } = xl;
  const canViewSync = user?.role === "Bedrijfsleiding"
    || user?.role === "Bedrijfsleiding 2"
    || user?.roleBase === "Bedrijfsleiding"
    || user?.roleBase === "Bedrijfsleiding 2";
  return (
    <>
      <div className="md:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center gap-3 px-4">
        <button data-testid="sidebar-toggle" onClick={() => setSidebarOpen(true)} className="p-1 text-slate-700"><Menu className="h-6 w-6" /></button>
        <img src="/clazing-logo.png" alt="Clazing" style={{ height: 28 }} className="w-auto object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <span className="font-bold text-slate-900">Clazing</span>
      </div>
      {sidebarOpen && <div className="md:hidden fixed inset-0 z-40 bg-black/40" data-testid="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`fixed inset-y-0 left-0 w-60 border-r border-slate-200 bg-white flex flex-col z-50 transform transition-transform duration-200 md:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-2">
          <img src="/clazing-logo.png" alt="Clazing logo" style={{ height: 36, maxHeight: 60 }} className="w-auto object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div><div className="font-bold text-slate-900 leading-none">Clazing</div><div className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Convenience Food B.V.</div></div>
          <button className="md:hidden ml-auto p-1 text-slate-500 hover:text-slate-800" data-testid="sidebar-close" onClick={() => setSidebarOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {TAB_GROUPS.map((grp) => {
            const visible = grp.ids
              .map((id) => TABS.find((t) => t[0] === id))
              .filter((t) => t && (!t[3] || t[3] === user?.role) && tabLevel(user?.role, t[0]) !== "none");
            if (!visible.length) return null;
            return (
              <div key={grp.title} className="mb-1">
                <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{grp.title}</div>
                {visible.map(([id, label, Icon]) => (
                  <button key={id} data-testid={`nav-${id}`} onClick={() => { setTab(id); if (id === "dashboard") setRemoteBadge(null); setSidebarOpen(false); }}
                    className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${tab === id ? "bg-[#1C64B0]/10 text-[#1C64B0] font-semibold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                    <Icon className="h-[18px] w-[18px]" strokeWidth={tab === id ? 2.25 : 1.75} /><span>{label}</span>
                    {id === "dashboard" && remoteBadge && <span data-testid="remote-update-dot" title={`Bijgewerkt door ${remoteBadge.by}`} className="ml-auto h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse" />}
                  </button>
                ))}
              </div>
            );
          })}
          <button data-testid="nav-mobiel" onClick={() => navigate("/mobiel")}
            className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 mt-2 border border-emerald-200">
            <Smartphone className="h-4 w-4" strokeWidth={1.75} /><span>Mobiele invoer</span>
          </button>
          {user?.crm && (
            <button data-testid="nav-sales" onClick={() => navigate("/sales")}
              className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 mt-2 border border-blue-200">
              <UsersRound className="h-4 w-4" strokeWidth={1.75} /><span>Sales CRM</span>
            </button>
          )}
          {(user?.role === "Bedrijfsleiding" || user?.role === "Bedrijfsleiding 2" || user?.roleBase === "Bedrijfsleiding" || user?.roleBase === "Bedrijfsleiding 2") && (
            <button data-testid="nav-erp" onClick={() => navigate("/erp")}
              className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 mt-2 border border-indigo-200">
              <Warehouse className="h-4 w-4" strokeWidth={1.75} /><span>ERP</span>
            </button>
          )}
        </nav>
        <div className="border-t border-slate-200 p-3">
          {online.length > 0 && (() => {
            const SRC_LABEL = { desktop: "Desktop", mobile: "Tablet", mobiel: "Tablet" };
            const groups = [];
            const byUser = {};
            online.forEach((o) => {
              const key = o.user_id || o.email || o.name;
              if (!byUser[key]) { byUser[key] = { name: o.name, is_self: o.is_self, sources: [] }; groups.push(byUser[key]); }
              const label = SRC_LABEL[o.source] || "Desktop";
              if (!byUser[key].sources.includes(label)) byUser[key].sources.push(label);
            });
            return (
            <div className="px-2 pb-3 mb-2 border-b border-slate-100" data-testid="online-users">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Online · {groups.length}</div>
              <div className="space-y-1">
                {groups.map((g, i) => (
                  <div key={g.name + i} className="flex items-center gap-2 text-xs text-slate-600" data-testid="online-user">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                    <span className="truncate">{g.name}{g.is_self ? " (jij)" : ""} · {g.sources.join(" + ")}</span>
                  </div>
                ))}
              </div>
            </div>
            );
          })()}
          <div className="px-2 pb-2"><div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div><span className="text-[10px] font-bold text-blue-700">{roleLabel(user?.role)}</span></div>
          <div className="px-2 pb-1"><span data-testid="app-version" className="text-[10px] text-slate-400" title={`Build ${APP_BUILD}`}>Versie {APP_VERSION} · {APP_BUILD}</span></div>
          <SyncOverview canView={canViewSync} />
          {(user?.role === "Bedrijfsleiding" || user?.role === "Bedrijfsleiding 2") && <ReleaseNoteAdmin />}
          <button data-testid="logout-button" onClick={async () => { if (await flushSave() === false) return; logout(); navigate("/login"); }} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-slate-600 hover:text-rose-600 hover:bg-slate-50"><LogOut className="h-4 w-4" /> Uitloggen</button>
        </div>
      </aside>
    </>
  );
}
